import { supabase } from '../lib/supabase';
import type { HistoryAction, HistoryEntityType } from '../types/projectHistory';

interface LogProjectHistoryParams {
  projectId: string;
  organizationId: string | null;
  action: HistoryAction;
  entityType: HistoryEntityType;
  entityName: string;
  description: string;
}

// getSession() reads the already-cached session from local storage — no network
// round-trip to the Auth server, unlike getUser(). At high mutation volume (many
// machines/bookings changing in sequence) that round-trip adds up for no benefit
// here: we only need the locally-known name, not a re-verified JWT.
async function resolveActorName(): Promise<{ actorUserId: string | null; actorName: string }> {
  const { data } = await supabase.auth.getSession();
  const user = data.session?.user;
  if (!user) return { actorUserId: null, actorName: 'Onbekende gebruiker' };
  const { first_name, last_name } = user.user_metadata ?? {};
  const name = [first_name, last_name].filter(Boolean).join(' ').trim();
  return { actorUserId: user.id, actorName: name || user.email || 'Onbekende gebruiker' };
}

// Never throws: a logging failure must never roll back or surface an error for the
// real mutation it's attached to. Callers should still `await` this (rather than
// fire-and-forget it) so the insert isn't racing page navigation/teardown — e.g. a
// user closing the tab right after confirming an action could otherwise cancel the
// in-flight request before it completes. Always call this after the real Supabase
// write has already succeeded.
export async function logProjectHistory({
  projectId,
  organizationId,
  action,
  entityType,
  entityName,
  description,
}: LogProjectHistoryParams): Promise<void> {
  if (!organizationId) return;
  try {
    const { actorUserId, actorName } = await resolveActorName();
    await supabase.from('project_history').insert({
      project_id: projectId,
      organization_id: organizationId,
      actor_user_id: actorUserId,
      actor_name: actorName,
      action,
      entity_type: entityType,
      entity_name: entityName,
      description,
    });
  } catch (e) {
    console.error('Kon wijziging niet loggen in project_history', e);
  }
}
